import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import OtherCostDropdown from '../../../../components/common/OtherCostDropdown';
import WarrantyPaymentTypeRadio from '../../../../components/common/WarrantyPaymentTypeRadio';
import { useLanguage } from '../../../../contexts/LanguageContext';

const ProformaOtherCostDialog = ({ visible, onHide, onSave, editingItem = null }) => {
    const { th, getCurrentLocale } = useLanguage();
    const initialForm = {
        itemCode: null,
        itemName: '',
        description: '',
        quantity: 1,
        price: 0,
        totalPrice: 0,
        warrantyType: null,
        type: 3
    };

    const [formData, setFormData] = useState(initialForm);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (!visible) {
            setFormData(initialForm);
            setSubmitted(false);
            return;
        }
        if (editingItem) {
            setFormData({
                id: editingItem.id,
                itemCode: editingItem.itemCode || '',
                itemName: editingItem.itemName || '',
                description: editingItem.description || '',
                quantity: editingItem.quantity ?? 1,
                price: editingItem.price ?? 0,
                totalPrice: editingItem.totalPrice ?? 0,
                warrantyType: editingItem.warrantyType,
                type: editingItem.type ?? 3
            });
        }
    }, [visible, editingItem]);

    const recalcTotal = (qty, price) => Math.round((qty || 0) * (price || 0) * 100) / 100;

    const handleSave = () => {
        setSubmitted(true);
        if (!formData.description || !formData.warrantyType) return;
        onSave({ ...formData, type: 3 });
    };

    const footer = (
        <div className="flex gap-2 justify-content-end w-full">
            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined />
            <Button label={editingItem ? th('common.update') : th('common.create')} icon="pi pi-check" onClick={handleSave} />
        </div>
    );

    return (
        <Dialog visible={visible} style={{ width: '40vw' }} header={th('proformaInvoice.addOtherCost')} modal className="p-fluid" footer={footer} onHide={onHide}>
            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.otherCosts.selectType')}</label>
                <OtherCostDropdown
                    value={formData.itemCode ? Number(formData.itemCode) : 0}
                    onChange={(val) => setFormData(prev => ({ ...prev, itemCode: String(val) }))}
                />
            </div>

            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.otherCosts.description')} *</label>
                <InputText value={formData.description || ''} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className={submitted && !formData.description ? 'p-invalid' : ''} />
                {submitted && !formData.description && <small className="p-error">{th('validation.required')}</small>}
            </div>

            <div className="grid">
                <div className="col-12 md:col-4">
                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.partDialog.quantity')}</label>
                        <InputNumber value={formData.quantity} onChange={(e) => {
                            const q = e.value || 0;
                            setFormData(prev => ({ ...prev, quantity: q, totalPrice: recalcTotal(q, prev.price) }));
                        }} min={0} />
                    </div>
                </div>
                <div className="col-12 md:col-4">
                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.otherCosts.price')}</label>
                        <InputNumber value={formData.price} onChange={(e) => {
                            const p = e.value || 0;
                            setFormData(prev => ({ ...prev, price: p, totalPrice: recalcTotal(prev.quantity, p) }));
                        }} mode="decimal" locale={getCurrentLocale()} minFractionDigits={0} maxFractionDigits={2} />
                    </div>
                </div>
                <div className="col-12 md:col-4">
                    <div className="field mb-2">
                        <label>{th('proformaInvoice.totalPrice')}</label>
                        <InputText value={formData.totalPrice ?? 0} disabled />
                    </div>
                </div>
            </div>

            <div className="field mb-2">
                <label className="font-bold block mb-2">{th('warrantyClaims.detail.partDialog.warrantyType')} *</label>
                <WarrantyPaymentTypeRadio
                    value={formData.warrantyType}
                    onChange={(val) => setFormData(prev => ({ ...prev, warrantyType: val }))}
                    className={submitted && !formData.warrantyType ? 'p-invalid' : ''}
                    excludeOptions={['3']}
                />
                {submitted && !formData.warrantyType && <small className="p-error">{th('validation.required')}</small>}
            </div>
        </Dialog>
    );
};

export default ProformaOtherCostDialog;
